export function getContentDispositionFileName(headers?: Headers | null): string | undefined {
  const header = headers?.get('content-disposition');

  if (!header) {
    return undefined;
  }

  const fileName =
    decodeRfc5987FileName(findHeaderParameter(header, 'filename*')) ??
    sanitizeAttachmentName(findHeaderParameter(header, 'filename'));

  return fileName || undefined;
}

function findHeaderParameter(header: string, parameter: string): string | undefined {
  const prefix = `${parameter}=`;

  return header
    .split(';')
    .map(part => part.trim())
    .find(part => part.toLowerCase().startsWith(prefix))
    ?.slice(prefix.length);
}

function decodeRfc5987FileName(rfc5987Value?: string): string | undefined {
  const unquotedFilename = sanitizeAttachmentName(rfc5987Value);

  if (!unquotedFilename) {
    return undefined;
  }

  // RFC 5987 format: charset'language'percent-encoded-value (e.g. UTF-8''TS_Chinese_%E4%B8%AD%E6%96%87.pdf)
  const match = /^([^']*)'[^']*'(.*)$/.exec(unquotedFilename);
  if (!match) {
    return undefined;
  }

  const [, charset, percentEncodedValue] = match;
  if (!charset || !percentEncodedValue) {
    return undefined;
  }

  const bytes: number[] = [];

  for (let index = 0; index < percentEncodedValue.length; index += 1) {
    const currentChar = percentEncodedValue[index];
    const hexValue = percentEncodedValue.slice(index + 1, index + 3);

    if (currentChar === '%' && /^[0-9a-f]{2}$/i.test(hexValue)) {
      bytes.push(Number.parseInt(hexValue, 16));
      index += 2;
      continue;
    }

    bytes.push(currentChar.codePointAt(0) ?? 0);
  }

  try {
    return new TextDecoder(charset).decode(new Uint8Array(bytes)) || undefined;
  } catch {
    return undefined;
  }
}

/**
 * This is the workaround due to a bug from dotnet runtime (https://github.com/dotnet/runtime/issues/32765)
 * Because of this, browser converts the quotes to underscore when user downloads the pdf.
 *
 * Current behaviour:
 * content-disposition header contains filename="filenameFromBackend"
 * browser converts "filenameFromBackend" => "_filenameFromBackend_"
 * user sees "_filenameFromBackend_.pdf" as the downloaded filename
 *
 * Expected behaviour:
 * content-disposition header contains filename=filenameFromBackend
 * browser converts "filenameFromBackend" => "filenameFromBackend"
 * user sees "filenameFromBackend.pdf" as the downloaded filename
 *
 * !!! Below line should be removed once the bug is fixed. !!!
 */
export function sanitizeAttachmentName(attachmentName?: string): string | undefined {
  if (!attachmentName) {
    return attachmentName;
  }

  if (attachmentName.startsWith('"') && attachmentName.endsWith('"')) {
    return attachmentName.slice(1, -1);
  }

  return attachmentName;
}
